import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { BadgeCheck, Sparkles, ShieldCheck, Loader2 } from "lucide-react";

export default function SalesRepSignupPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    pitch: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) {
      toast.error("Please fill name, email, and a password (min 6 chars)");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/sales-rep/signup", form);
      localStorage.setItem("ipv_token", data.token);
      toast.success(data.message || "Application received!");
      // Light reload so AuthContext re-fetches user
      window.location.href = "/sales-rep/dashboard";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit application");
    } finally {
      setSubmitting(false);
    }
  };

  if (user?.role === "sales_rep") {
    navigate("/sales-rep/dashboard", { replace: true });
    return null;
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-12" data-testid="sales-rep-signup-page">
      <div className="grid lg:grid-cols-[1fr_460px] gap-12">
        {/* Pitch column */}
        <div>
          <div className="ipv-eyebrow text-[#0A1C40]" data-testid="rep-page-eyebrow">
            Become a Sales Rep
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-3">
            Earn <span className="text-[#FFC220]">10%</span> on every customer you bring in.
          </h1>
          <p className="mt-6 text-lg text-gray-700 max-w-xl">
            Share your unique link. When someone buys <strong>Furniture</strong> or a{" "}
            <strong>Scooter</strong> through it, you earn a flat 10% commission on those items.
            Direct sales only — no overrides, no fine print.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="border-2 border-gray-200 p-5">
              <Sparkles className="w-5 h-5 text-[#FFC220]" />
              <div className="mt-3 font-bold">Flat 10%</div>
              <div className="text-sm text-gray-600 mt-1">
                On the Furniture & Scooters subtotal of every paid order you refer.
              </div>
            </div>
            <div className="border-2 border-gray-200 p-5">
              <BadgeCheck className="w-5 h-5 text-[#0A1C40]" />
              <div className="mt-3 font-bold">Your own link</div>
              <div className="text-sm text-gray-600 mt-1">
                Once approved you get a unique link to share on socials, group chats, or in person.
              </div>
            </div>
            <div className="border-2 border-gray-200 p-5">
              <ShieldCheck className="w-5 h-5 text-green-700" />
              <div className="mt-3 font-bold">Live earnings</div>
              <div className="text-sm text-gray-600 mt-1">
                Track every commission in real time from your rep dashboard.
              </div>
            </div>
          </div>

          <div className="mt-10 bg-blue-50 border-l-4 border-[#0A1C40] p-5 text-sm text-gray-700 max-w-xl" data-testid="rep-rules-box">
            <div className="font-bold text-[#0A1C40] mb-2">Quick rules</div>
            <ul className="list-disc pl-5 space-y-1">
              <li>Pallets, bins, and general merch <strong>don't</strong> earn commission.</li>
              <li>Direct referrals only — no multi-level or overrides.</li>
              <li>Admin approves your application within 24 hours.</li>
              <li>We pay out at the end of every month.</li>
            </ul>
          </div>
        </div>

        {/* Signup form */}
        <aside>
          <form
            onSubmit={onSubmit}
            className="border-2 border-[#0A1C40] p-6 lg:p-8 bg-white sticky top-24"
            data-testid="rep-signup-form"
          >
            <div className="font-display font-black text-2xl tracking-tight">Apply now</div>
            <p className="text-sm text-gray-600 mt-1">
              Takes less than a minute. We'll review and email you within 24 hours.
            </p>

            <label className="block mt-6 text-xs font-bold tracking-widest text-gray-700 uppercase">Full Name</label>
            <input
              className="input-base mt-2"
              placeholder="Sarah Smith"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="rep-signup-name"
              required
            />

            <label className="block mt-4 text-xs font-bold tracking-widest text-gray-700 uppercase">Email</label>
            <input
              className="input-base mt-2"
              type="email"
              placeholder="you@email.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="rep-signup-email"
              required
            />

            <label className="block mt-4 text-xs font-bold tracking-widest text-gray-700 uppercase">Password</label>
            <input
              className="input-base mt-2"
              type="password"
              placeholder="Min 6 characters"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="rep-signup-password"
              required
              minLength={6}
            />

            <label className="block mt-4 text-xs font-bold tracking-widest text-gray-700 uppercase">Phone (optional)</label>
            <input
              className="input-base mt-2"
              placeholder="(616) 555-1234"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="rep-signup-phone"
            />

            <label className="block mt-4 text-xs font-bold tracking-widest text-gray-700 uppercase">Why you'd be great (optional)</label>
            <textarea
              className="input-base mt-2"
              rows={3}
              placeholder="Tell us about your network or why you'd be a great rep..."
              value={form.pitch}
              onChange={(e) => setForm({ ...form, pitch: e.target.value })}
              data-testid="rep-signup-pitch"
              maxLength={500}
            />

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2"
              data-testid="rep-signup-submit"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Submit application
            </button>

            <p className="text-xs text-gray-500 mt-4 text-center">
              Already a rep? <Link to="/login" className="font-bold text-[#0A1C40] underline">Sign in</Link>
            </p>
          </form>
        </aside>
      </div>
    </div>
  );
}
